import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import moment from 'moment';
import Api from 'services/api';
import ViewRequestTable from 'views/appointment/view/ViewRequestTable';
import { SubmitButton } from 'components/common/buttons/SubmitButton';
import { approveLanguageRequest, rejectLanguageRequest } from 'slices/languageRequestSlice';
import toast from 'react-hot-toast';
import FullPageLoader from "components/common/FullPageLoader";
import { useTranslation } from 'react-i18next';

const LanguageRequestView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [item, setItem] = useState({});
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [viewCancelModalOpen, setViewCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  React.useEffect(() => {
    fetchRequestDetails();
  }, [id]);

  const fetchRequestDetails = async () => {
    setLoading(true);
    try {
      const result = await Api.getAppointmentRequest(id);
      if (result?.status === 200 && result?.data?.data) {
        const data = result.data.data;
        setItem({
          ...data,
          appointment_details: data.request_details,
          appointment_custom_pricing: data.request_custom_pricing,
          timezone_id: data.request_details?.timezone_id,
        });
      } else {
        toast.error('Language request not found');
        navigate('/language-requests');
      }
    } catch (error) {
      toast.error('Error loading language request');
      console.error(error);
      navigate('/language-requests');
    } finally {
      setLoading(false);
    }
  };

  // if (loading) {
  //   return (
  //     <div className="content">
  //       <div className="loading-container">
  //         <p>Loading appointment request details...</p>
  //       </div>
  //     </div>
  //   );
  // }

  const openRejectModal = () => {
    setRejectReason('');
    setRejectModalOpen(true);
  };

  const closeRejectModal = () => {
    setRejectModalOpen(false);
    setRejectReason('');
  };

  const openViewCancelModal = () => {
    setCancelReason(item?.request_details?.cancel_reason || '');
    setViewCancelModalOpen(true);
  };

  const closeViewCancelModal = () => {
    setViewCancelModalOpen(false);
    setCancelReason('');
  };

  const handleApprove = () => {
    setActionLoading(true);
    dispatch(
      approveLanguageRequest(item.id, (status, message) => {
        if (status) {
          toast.success(message || 'Language request moved to client request');
          navigate('/appointment-requests');
        } else {
          toast.error(message || 'Unable to move request');
        }
        setActionLoading(false);
      })
    );
  };

  const handleRejectSubmit = () => {
    if (!rejectReason.trim()) {
      toast.error('Please enter a rejection reason');
      return;
    }
    setActionLoading(true);
    dispatch(
      rejectLanguageRequest(item.id, { reason: rejectReason }, (status, message) => {
        if (status) {
          toast.success(message || 'Language request cancelled');
          closeRejectModal();
          fetchRequestDetails();
        } else {
          toast.error(message || 'Unable to cancel request');
        }
        setActionLoading(false);
      })
    );
  };

  const AppointmentTimezone = item?.appointment_details?.timezone;
  const rawDatetime = item?.datetime;

  const formattedDate = AppointmentTimezone?.name
      ? moment.tz(rawDatetime, AppointmentTimezone.name).format("MMM D, YYYY")
      : moment(rawDatetime).format("MMM D, YYYY");

  const formattedDefaultTime = AppointmentTimezone?.name
    ? moment.tz(rawDatetime, AppointmentTimezone.name).tz("America/Denver").format("hh:mm A")
    : moment(rawDatetime).format("hh:mm A");

  const formattedClientTime = (() => {
      if (!AppointmentTimezone?.name) return formattedDefaultTime;
      const m = moment.tz(rawDatetime, AppointmentTimezone.name);
      const dynamicAbbr = m.format("z");
      const dynamicOffset = m.format("Z");
      
      const zone = dynamicAbbr ? `(${dynamicAbbr} / GMT${dynamicOffset})` : `(GMT${dynamicOffset})`;
      return `${m.format("hh:mm A")} ${zone}`;
    })();

  return (
    <div className="content">
      {loading && <FullPageLoader />}
      {/* Header with Title */}
      <div className="row_main_head bg-primary-secondaryCard">
        <h2 className="main_heading main_heading1">
          {t("requestView.heading")} {item?.appid || t("common.nA")}
        </h2>
        <div className="mb-3">
          <div className="text-transform-capitalize main_heading main_heading1">
            {t("requestView.client")}: {item?.accounts?.user?.name || t("common.nA")}
          </div>
        </div>
      </div>

      {/* Date and Time Info */}
      <div className="row_main_head bg-light-secondaryCard mt-3">
        <h2 className="main_heading2 view-page">
          {t("table.date")} <span className="dateTime viewpage_tags">{formattedDate}</span> /{" "}
          <span className="dateTime viewpage_tags">{formattedDefaultTime}</span> {t("requestView.or")} {" "}
          <span className="dateTime viewpage_tags">{formattedClientTime}</span>
        </h2>
        <div className="">
          <div className="text-transform-capitalize main_heading2 d-flex align-items-center">
            <span className="appointmentType viewpage_tags">
              {item?.type === 'Telephonic'
              ? 'Telephonic'
              : item?.type || 'N/A'}
            </span>
            <span className="appLanguage viewpage_tags">{item?.language?.name ? item?.language?.name : item?.language_requests?.[0]?.language_name || "N/A"}</span>
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="card_table view_table_custom">
        {/* Action Buttons */}
        <div className="row-primary flex-wrap justify-content-between">
          <div className="row-primary flex-wrap">
            {item?.status !== 'approved' && item?.status !== 'cancelled' && (
              <>
                <button type="button" className="main_button  tomatoRed" onClick={openRejectModal} disabled={actionLoading}>
                  {t("requestView.cancelRequest")}
                </button>
                <button type="button" className="main_button" onClick={handleApprove} disabled={actionLoading}>
                  {actionLoading ? t("requestView.moving") : t("requestView.moveToClientRequest")}
                </button>
              </>
            )}
            {/* {item?.status === 'cancelled' && item?.request_details?.cancel_reason && (
              <button type="button" className="main_button green" onClick={openViewCancelModal}>
                View Reason
              </button>
            )} */}
          </div>
        </div>

        {/* Details Table */}
        <ViewRequestTable item={item} isRequestView={true} />
      </div>

      <Modal isOpen={rejectModalOpen} toggle={closeRejectModal} centered className="modal-medium">
        <ModalHeader toggle={closeRejectModal}><h5 className="modal-title_u">{t("requestView.cancelLanguageRequest")}</h5></ModalHeader>
        <ModalBody className="modal_form_body">
          <div className="min-height-auto" style={{ backgroundColor: 'rgb(242, 242, 242)', padding: '10px', borderRadius: '8px' }}>
            <h2>{t("translation.appointmentId")}: {item?.appid}</h2>
            <div className="row-primary">
              <h2>{t("translation.reason")}</h2>
              <textarea
                className="input-in type_inp"
                rows={5}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder={t("requestView.cancelReasonPlaceholder")}
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <button type="button" className="main_button  tomatoRed" onClick={handleRejectSubmit} disabled={actionLoading || !rejectReason.trim()}>
            {t("billing.cancel")}
          </button>
          <button type="button" className="Cancle_button" onClick={closeRejectModal}>
            {t("translation.close")}
          </button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={viewCancelModalOpen} toggle={closeViewCancelModal} centered className="modal-medium">
        <ModalHeader toggle={closeViewCancelModal}><h5 className="modal-title_u">{t("requestView.cancelReason")}</h5></ModalHeader>
        <ModalBody className="modal_form_body">
          <div className="row-primary">
            <h2>{t("translation.reason")}</h2>
            <div className="type_inp input-in pt-3" style={{ minHeight: 120, whiteSpace: 'pre-wrap' }}>
              {cancelReason}
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="row_shift Step_change modal-footer">
          <button type="button" className="Cancle_button" onClick={closeViewCancelModal}>
            {t("translation.close")}
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default LanguageRequestView;
